import React, { useState } from 'react';
import { useNavigate, useLoaderData, Form, useActionData, LoaderFunctionArgs, ActionFunctionArgs } from 'react-router';
import { apiClient } from '../../../lib/api-client';
import { requireUser } from '../../../lib/session.server';

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  return { user };
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const intent = formData.get('intent');
  
  try {
    if (intent === 'update-email') {
      const email = formData.get('email');
      const password = formData.get('password');
      
      await apiClient.put('/account/email', { email, password });
      return { success: true, message: 'Email updated successfully' };
    }
    
    if (intent === 'delete-account') {
      const password = formData.get('password');
      
      await apiClient.delete('/account', { 
        body: { password, confirmation: 'DELETE' }
      });
      
      return { deleted: true };
    }
    
    return { error: 'Invalid action' };
  } catch (error: any) {
    return { error: error.message || 'Operation failed' };
  }
}

export function SettingsAccountPage() {
  const { user } = useLoaderData() as any;
  const actionData = useActionData() as any;
  const navigate = useNavigate();
  
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [email, setEmail] = useState(user?.email || '');
  const [password, setPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');

  React.useEffect(() => {
    if (actionData?.deleted) {
      navigate('/login');
    }
  }, [actionData, navigate]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Account Settings</h1>
        <p className="text-gray-600">Manage your account preferences and security</p>
      </div>

      {actionData?.success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {actionData.message}
        </div>
      )}

      {actionData?.error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {actionData.error}
        </div>
      )}

      <div className="space-y-6">
        {/* Email Update Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Email Address</h2>
          
          <Form method="post" className="space-y-4">
            <input type="hidden" name="intent" value="update-email" />
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Email Address
              </label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <input
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your current password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Update Email
            </button>
          </Form>
        </div>

        {/* Account Info Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Account Information</h2>
          
          <div className="space-y-3">
            <div>
              <span className="text-sm text-gray-600">Account ID:</span>
              <p className="font-mono text-sm">{user?.id}</p>
            </div>
            
            <div>
              <span className="text-sm text-gray-600">Member Since:</span>
              <p className="text-sm">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            
            <div>
              <span className="text-sm text-gray-600">Last Updated:</span>
              <p className="text-sm">
                {user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Data Export Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Data Management</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2">Export Your Data</h3>
              <p className="text-sm text-gray-600 mb-3">
                Download a copy of all your data including profile, resumes, and applications.
              </p>
              <button
                onClick={() => alert('Data export feature coming soon')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Request Data Export
              </button>
            </div>
          </div>
        </div>

        {/* Delete Account Section */}
        <div className="bg-white rounded-lg shadow p-6 border-2 border-red-100">
          <h2 className="text-xl font-semibold mb-4 text-red-600">Danger Zone</h2>
          
          <div>
            <h3 className="font-medium mb-2">Delete Account</h3>
            <p className="text-sm text-gray-600 mb-3">
              Once you delete your account, there is no going back. All your data will be permanently removed.
            </p>
            
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete Account
              </button>
            ) : (
              <Form method="post" className="space-y-4 mt-4 p-4 bg-red-50 rounded-lg">
                <input type="hidden" name="intent" value="delete-account" />
                
                <div className="text-sm text-red-700">
                  <p className="font-semibold mb-2">Are you absolutely sure?</p>
                  <p>This action cannot be undone. This will permanently delete your account and remove all associated data.</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-red-700 mb-1">
                    Enter your password to confirm
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    required
                    placeholder="Enter your password"
                    className="w-full px-3 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
                
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Yes, Delete My Account
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeletePassword('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}